import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import SitePage, { CallToAction, calendly, styles, T, ACCENT } from '../components/site/SitePage';

const PATH = '/alternative-stocky';
// Date de dernière mise à jour du contenu : visible sur la page et lue par Google (dateModified).
// À changer à chaque modification réelle du calcul ou du texte.
const MAJ = '2026-10-08';
const majFr = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(`${MAJ}T12:00:00`));


// Dates tirées des annonces de Shopify, reprises par la presse spécialisée (vérifiées le 2026-10-08).
const TIMELINE = [
  ['Juillet 2025', 'Shopify retire une partie des fonctions de Stocky, dont les transferts de stock et une partie des prévisions.'],
  ['2 février 2026', "Stocky disparaît de l'App Store Shopify : plus aucune nouvelle installation."],
  ['31 août 2026', "Arrêt complet. Les données qui n'avaient pas été exportées ne sont plus accessibles."]
];

const OPTIONS = [
  ["L'admin Shopify", 'Le suivi des quantités et des bons de commande simples, sans application à ajouter.', "Une seule boutique, peu de références, un fournisseur proche : c'est souvent suffisant.", 'Pas de prévision des ventes, pas de point de commande par produit, rien sur Amazon.'],
  ['Une application de prévision', "Les applications de l'App Store Shopify qui prévoient les ventes et proposent des commandes.", 'Une marque qui vend surtout sur Shopify et veut une solution prête à l’emploi.', "Elles voient mal Amazon et le B2B, et l'abonnement grimpe avec le volume."],
  ['Un logiciel de gestion (ERP)', 'Un système complet : stock, achats, facturation, comptabilité.', 'Une entreprise déjà structurée, avec plusieurs entrepôts et une équipe pour le faire vivre.', 'Coût et durée de mise en place, et une façon de travailler à adopter.'],
  ['Un outil de réappro sur mesure', 'Construit sur vos ventes Shopify, Amazon et B2B, vos délais et vos règles fournisseurs.', 'Une marque qui vend sur plusieurs canaux depuis le même stock.', 'Un projet à cadrer : comptez trois semaines pour un premier outil.']
];

const FAQ = [
  ['Stocky fonctionne-t-il encore ?', "Non. Shopify a arrêté Stocky le 31 août 2026, après l'avoir retiré de l'App Store le 2 février 2026."],
  ['Mes données Stocky sont-elles perdues ?', "Ce qui n'avait pas été exporté avant l'arrêt n'est plus accessible dans Stocky. Vos produits, vos commandes et vos quantités en stock, eux, restent dans Shopify : c'est la base pour repartir."],
  ["Par quoi remplacer Stocky si je vends aussi sur Amazon ?", "Par un outil qui voit les deux canaux depuis le même stock : la prévision additionne les ventes Shopify et Amazon, et compte le stock déjà dans les entrepôts Amazon. Les applications pensées pour Shopify seul le font rarement bien."],
  ["Comment calculer un point de commande sans Stocky ?", "Avec vos ventes des dernières semaines et le délai de votre fournisseur : le point de commande, c'est ce que vous vendez pendant le délai, plus un stock de sécurité. Le calculateur gratuit du site le fait pour un produit, avec la feuille Excel."],
  ['Combien coûte un outil de réappro sur mesure ?', 'À partir de 2 900 € HT pour un premier outil, livré en 3 semaines, à prix fixe écrit avant de commencer. Le code vous appartient.']
];

const ARTICLE_LD = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: "Alternative à Stocky : par quoi remplacer l'outil de stock de Shopify ?",
  datePublished: '2026-10-08',
  dateModified: MAJ,
  inLanguage: 'fr-FR',
  author: { '@type': 'Person', name: 'Mattéo', url: 'https://autoria.dev/about' },
  publisher: { '@type': 'Organization', name: 'Autoria', url: 'https://autoria.dev', logo: { '@type': 'ImageObject', url: 'https://autoria.dev/apple-touch-icon.png' } },
  image: 'https://autoria.dev/og-image.png',
  mainEntityOfPage: `https://autoria.dev${PATH}`
};

const FAQ_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } }))
};

const StockyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const P = ({ children }) => <p style={{ margin: '16px 0 0', fontSize: 18, color: T.text, lineHeight: 1.6 }}>{children}</p>;

  return (
    <SitePage>
      <SEO
        title="Alternative à Stocky : remplacer l'outil de stock Shopify"
        description="Shopify a fermé Stocky le 31 août 2026. Les options pour le remplacer, et laquelle choisir si vous vendez aussi sur Amazon ou en B2B."
        path={PATH}
        breadcrumb={[['Blog', '/blog'], ['Alternative à Stocky', PATH]]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
        <script type="application/ld+json">{JSON.stringify(ARTICLE_LD)}</script>
      </Helmet>

      <section style={{ ...styles.narrow, padding: '56px 24px 0', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <p style={styles.kicker}>Shopify · gestion de stock · mis à jour le {majFr}</p>
        <h1 style={styles.h1}>Alternative à Stocky : par quoi remplacer l'outil de stock de Shopify ?</h1>
        <p style={styles.lead}>Shopify a fermé Stocky le 31 août 2026. Si vous vous en serviez pour savoir quoi commander à vos fournisseurs, voici les options, et quand chacune suffit.</p>
      </section>

      <article style={{ ...styles.narrow, paddingTop: 40 }}>
        <h2 style={styles.h2}>Ce qui s'est passé</h2>
        <ol style={{ margin: '24px 0 0', padding: 0, listStyle: 'none', borderLeft: `2px solid ${ACCENT}` }}>
          {TIMELINE.map(([date, text]) => (
            <li key={date} style={{ padding: '0 0 18px 20px', position: 'relative' }}>
              <span style={{ position: 'absolute', left: -7, top: 6, width: 12, height: 12, background: ACCENT, borderRadius: 2 }} />
              <p style={{ margin: 0, fontFamily: T.mono, fontSize: 13, color: ACCENT }}>{date}</p>
              <p style={{ margin: '4px 0 0', fontSize: 17, color: T.text }}>{text}</p>
            </li>
          ))}
        </ol>
        <P>Shopify reprend la gestion de stock de base dans l'admin et la caisse : le suivi des quantités et les bons de commande. Ce qui ne revient pas : la prévision des ventes et le calcul de ce qu'il faut commander, et quand.</P>

        <h2 style={{ ...styles.h2, marginTop: 56 }}>Les options, et quand chacune suffit</h2>
      </article>

      <section style={{ ...styles.container, paddingTop: 28 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 16 }}>
          {OPTIONS.map(([titre, quoi, pourQui, limite], i) => (
            <div key={titre} style={{ background: '#FFFFFF', border: i === 3 ? `2px solid ${ACCENT}` : `1px solid ${T.line2}`, borderRadius: 8, padding: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span style={{ fontFamily: T.mono, fontSize: 13, color: ACCENT }}>0{i + 1}</span>
              <p style={{ margin: 0, fontSize: 19, fontWeight: 800, fontStretch: '88%' }}>{titre}</p>
              <p style={{ margin: 0, fontSize: 15, color: T.text }}>{quoi}</p>
              <p style={{ margin: 0, fontSize: 14, color: T.text }}><strong>Suffit pour :</strong> {pourQui}</p>
              <p style={{ margin: 0, fontSize: 14, color: T.muted }}><strong>Limite :</strong> {limite}</p>
            </div>
          ))}
        </div>
      </section>

      <article style={{ ...styles.narrow, paddingTop: 16 }}>
        <h2 style={{ ...styles.h2, marginTop: 56 }}>Si vous vendez aussi sur Amazon</h2>
        <P>C'est là que la plupart des remplaçants décrochent. Une marque qui vend sur Shopify et sur Amazon depuis le même stock a besoin de trois choses qu'une application Shopify voit mal :</P>
        <ul style={{ margin: '12px 0 0', paddingLeft: 20, listStyle: 'disc', fontSize: 17, color: T.text, lineHeight: 1.7 }}>
          <li><strong>les ventes des deux canaux additionnées</strong>, avec la saison de chacun ;</li>
          <li><strong>le stock déjà chez Amazon</strong>, et celui en route vers ses entrepôts ;</li>
          <li><strong>les délais réels</strong> : fabrication, transport, et réception par Amazon, qui s'allonge en fin d'année.</li>
        </ul>
        <P>C'est exactement ce que fait <Link to="/reappro-prevision-stock">un outil de réappro sur mesure</Link> : chaque lundi, la liste de ce qu'il faut commander, en quelle quantité, et quand, sur tout le catalogue.</P>

        <h2 style={{ ...styles.h2, marginTop: 56 }}>Par où commencer cette semaine</h2>
        <ol style={{ margin: '16px 0 0', paddingLeft: 22, fontSize: 17, color: T.text, lineHeight: 1.7 }}>
          <li><strong>Rassemblez ce que vous avez</strong> : vos derniers bons de commande, les délais et les minimums de chaque fournisseur.</li>
          <li><strong>Calculez le point de commande de vos best-sellers</strong> avec le <Link to="/outils/calcul-stock-de-securite">calculateur gratuit</Link>, et recevez le modèle Excel pour le faire sur cinquante produits.</li>
          <li><strong>Choisissez selon votre situation</strong> : l'admin Shopify pour quelques références, une application si vous ne vendez que sur Shopify, un outil sur mesure si vous vendez aussi sur Amazon ou en B2B.</li>
        </ol>
        <div style={{ marginTop: 28, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <a href={calendly('stocky')} target="_blank" rel="noopener" className="lift" style={styles.button}>En parler 30 minutes</a>
          <Link to="/outils/calcul-stock-de-securite" className="lift" style={{ ...styles.button, background: '#FFFFFF', color: T.ink, border: `1px solid ${T.ink}`, fontWeight: 600 }}>Calculer mon point de commande</Link>
        </div>

        <h2 id="faq" style={{ ...styles.h2, marginTop: 56 }}>Questions fréquentes</h2>
        <div style={{ marginTop: 20, borderTop: `1px solid ${T.line2}` }}>
          {FAQ.map(([q, a]) => (
            <details key={q} style={{ borderBottom: `1px solid ${T.line2}`, padding: '16px 0' }}>
              <summary style={{ cursor: 'pointer', fontSize: 18, fontWeight: 700 }}>{q}</summary>
              <p style={{ margin: '10px 0 0', fontSize: 17, color: T.text, lineHeight: 1.6 }}>{a}</p>
            </details>
          ))}
        </div>
      </article>

      <CallToAction from="stocky-bas" title="Vous utilisiez Stocky ?" text="30 minutes pour voir quel remplaçant colle à votre situation, même s'il ne s'agit pas d'un outil sur mesure." />
    </SitePage>
  );
};

export default StockyPage;
